import type React from "react";
import { useState } from "react";

import type { ConnectedProjectDto, UserProjectDto } from "@/api/types";
import { FormRadioCardGroup } from "@/components/Form/FormRadioCard";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { RadioCard } from "@/components/Radio/RadioCard";
import { Button } from "@/design-system/Button";
import { Notice } from "@/design-system/Notice";
import { Stack } from "@/design-system/Stack";
import { Trans, useTranslation } from "@/translations";

enum EditType {
  ALL_PROJECTS = "ALL_PROJECTS",
  CURRENT_PROJECT = "CURRENT_PROJECT",
}

type EditUserConfirmationModalProps = ModalBaseProps & {
  projects: UserProjectDto[] | ConnectedProjectDto[];
  isSubmitting: boolean;
  changedEmail?: string;
  onConfirm: (applyToAllProjects: boolean) => void;
};

export function EditUserConfirmationModal({
  isOpened,
  onOpenChange,
  isSubmitting,
  projects,
  changedEmail,
  onConfirm,
}: EditUserConfirmationModalProps): React.ReactNode {
  const [selectedType, setSelectedType] = useState<EditType>(EditType.ALL_PROJECTS);

  const { t } = useTranslation();

  return (
    <Modal.Root
      title={t("page.edit-user.edit-confirmation-modal.title")}
      data-testid="confirm-edit-modal"
      isOpened={isOpened}
      onOpenChange={onOpenChange}
    >
      <Modal.Body>
        <Stack gap="md">
          <p className="text-body">{t("page.edit-user.edit-confirmation-modal.body")}</p>
          <FormRadioCardGroup>
            <RadioCard
              name="selectedType"
              value={EditType.ALL_PROJECTS}
              checked={selectedType === EditType.ALL_PROJECTS}
              onChange={() => setSelectedType(EditType.ALL_PROJECTS)}
              label={t("page.edit-user.edit-confirmation-modal.form.option.apply-all-projects", {
                count: projects.length,
              })}
              data-testid="apply-to-all-projects-option"
            />
            <RadioCard
              name="selectedType"
              value={EditType.CURRENT_PROJECT}
              checked={selectedType === EditType.CURRENT_PROJECT}
              onChange={() => setSelectedType(EditType.CURRENT_PROJECT)}
              label={t("page.edit-user.edit-confirmation-modal.form.option.apply-current-project")}
              data-testid="apply-to-current-project-option"
            />
          </FormRadioCardGroup>
          {selectedType === EditType.CURRENT_PROJECT && changedEmail && (
            <Notice
              variant="warning"
              customBody={
                <span>
                  <Trans
                    i18nKey="page.edit-user.edit-confirmation-modal.email-current-project-only-warning"
                    values={{ email: changedEmail }}
                  />
                </span>
              }
            />
          )}
          {selectedType === EditType.ALL_PROJECTS && (
            <Notice
              variant="special"
              customBody={
                <Stack gap="sm">
                  <p>{t("page.edit-user.edit-confirmation-modal.accordion.edit-details.intro")}</p>
                  <Stack gap="xs" as="ul">
                    {[
                      t("page.create-user.form.first-name.label"),
                      t("page.create-user.form.last-name.label"),
                      changedEmail ? t("page.create-user.form.email.label") : null,
                      t("page.create-user.form.language.label"),
                      t("page.create-user.form.auto-translation-language.label"),
                      t("page.create-user.form.avatar.label"),
                    ]
                      .filter(Boolean)
                      .map((label) => (
                        <li className="ml-space-xl list-disc" key={label}>
                          {label}
                        </li>
                      ))}
                  </Stack>
                  <p className="italic">
                    {t("page.edit-user.edit-confirmation-modal.accordion.edit-details.disclaimer")}
                  </p>
                </Stack>
              }
            />
          )}
        </Stack>
      </Modal.Body>
      <Modal.Actions>
        <Button
          onClick={() => onConfirm(selectedType === EditType.ALL_PROJECTS)}
          isLoading={isSubmitting}
          data-testid="confirm-btn"
        >
          {selectedType === EditType.CURRENT_PROJECT
            ? t("page.edit-user.edit-confirmation-modal.btn.apply-to-current-project")
            : t("page.edit-user.edit-confirmation-modal.btn.apply-to-all-projects")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
